import { datadogRum } from '@datadog/browser-rum';
import React, { useCallback, useMemo } from 'react';

import {
  MessagingDepsProvider,
  type MessagingLogger,
  type MessagingMetrics,
  type MessagingTransport,
} from '@dutchie-pos/messaging';

import { post } from 'src/utils/helpers/http';
import { useBaseAPIParams } from 'src/utils/hooks/useBaseAPIParams';

import { useLogger } from './logger';

// datadogRum.addAction is mobile-checkout's equivalent of an addCount metric.
const messagingMetrics: MessagingMetrics = {
  addCount: (name, options) => datadogRum.addAction(name, options),
};

export function MessagingProvider({ children }: { children: React.ReactNode }) {
  const appLogger = useLogger();
  const { LspId, LocId } = useBaseAPIParams();

  // Wraps mobile-checkout's `post` helper to match the transport contract the
  // messaging lib expects. LspId/LocId are injected here because createAuthBody
  // does not include them — the messaging config/auth endpoints need them to
  // construct the correct per-location channel name.
  const messagingTransport: MessagingTransport = useCallback(
    (path, body) => post(path, { ...(body as object), LspId, LocId }),
    [LspId, LocId],
  );

  // Bridge mobile-checkout's Logger class to the messaging lib's MessagingLogger
  // interface. The Logger's warn/info/debug require a { key } field in their
  // data arg (BaseCustomEventData) — we supply a stable 'messaging' key so the
  // rest of context flows through unchanged.
  const messagingLogger: MessagingLogger = useMemo(
    () => ({
      error: (message, context) => appLogger.error(message, context),
      warn: (message, context) => appLogger.warn(message, { key: 'messaging', ...context } as any),
      info: (message, context) => appLogger.info(message, { key: 'messaging', ...context } as any),
      debug: (message, context) => appLogger.debug(message, { key: 'messaging', ...context } as any),
    }),
    [appLogger],
  );

  return (
    <MessagingDepsProvider transport={messagingTransport} logger={messagingLogger} metrics={messagingMetrics}>
      {children}
    </MessagingDepsProvider>
  );
}
